import createFetchival from '@exodus/fetch/create-fetchival';
import { fetch as exodusFetch } from '@exodus/fetch';
import { EXODUS_NFTS_PROXY_BASE_URL, Networks, SUPPORTED_EVM_NETWORKS, SUPPORTED_NETWORKS, } from './constants.js';
export class NftsProxyApi {
    #fetchival;
    #baseUrl;
    api = {};
    /**
     * networks is the list of network names, using Networks as default for non breaking change. Wallet should provide their own list, e.g. ME's BE only cares about solana, ethereum and bitcoin.
     */
    constructor({ baseUrl, fetch = exodusFetch, networks = SUPPORTED_NETWORKS } = {
        networks: SUPPORTED_NETWORKS,
    }) {
        this.#fetchival = createFetchival({ fetch });
        this.#baseUrl = baseUrl || EXODUS_NFTS_PROXY_BASE_URL;
        networks.forEach((network) => {
            this.api[network] = this._createNftProxyNetworkClient(network);
            // @ts-ignore // so clients can keep calling like nftProxy.solana.getNftImage like it used to.
            this[network] = this.api[network]; // @deprecated, use nftProxy.api.solana.getNftImage
        });
    }
    request(url, queryParams, options) {
        return this.#fetchival(url, options).get(queryParams);
    }
    getNetwork(nft) {
        return typeof nft === 'string' ? nft.split(':')[0] : nft.network;
    }
    isEvm(nft) {
        const network = this.getNetwork(nft);
        return SUPPORTED_EVM_NETWORKS.includes(network);
    }
    decomposeId(nftId) {
        const [network, pseudoId] = nftId.split(':');
        if (SUPPORTED_EVM_NETWORKS.includes(network) || network === Networks.Tezos) {
            const [contractAddress, tokenId] = pseudoId.split('/');
            return { network, contractAddress, tokenId };
        }
        if (network === Networks.Algorand) {
            return { network, tokenId: pseudoId };
        }
        if (network === Networks.Solana) {
            return { network, mintAddress: pseudoId };
        }
        if (network === Networks.Cardano) {
            return { network, tokenId: pseudoId };
        }
        if (network === Networks.Bitcoin) {
            return { network, tokenId: pseudoId };
        }
        throw new Error('NftsProxyApi.decomposeId: Unknown network');
    }
    getNft(nftId) {
        const [network, pseudoId] = nftId.split(':');
        const idParts = pseudoId.split('/');
        return this.request(`${this.#baseUrl}/${encodeURIComponent(network)}/nfts/${idParts
            .map(encodeURIComponent)
            .join('/')}`);
    }
    getNftImage(nftId, size, opts) {
        const headers = {};
        if (opts.fetchQoi) {
            headers['Accept'] = 'image/qoi';
        }
        return this.request(this.getNftImageUrl(nftId, size, opts), {}, { headers });
    }
    getNftImageUrl(nftId, size, { fetchQoi } = {}) {
        const [network, pseudoId] = nftId.split(':');
        const idParts = pseudoId.split('/');
        const params = [...idParts, Number(size)];
        const url = new URL(`${this.#baseUrl}/${encodeURIComponent(network)}/nfts/${params
            .map(encodeURIComponent)
            .join('/')}`);
        if (fetchQoi) {
            url.searchParams.append('qoi', 'true');
        }
        return url.toString();
    }
    _createNftProxyNetworkClient(network) {
        const encodedNetwork = encodeURIComponent(network);
        return {
            getNftsByOwner: (address, params = {}) => {
                const encodedAddress = encodeURIComponent(address);
                const queryParams = {};
                if (params.includeListed) {
                    queryParams.includeListed = 'true';
                }
                if (params.includeSpam) {
                    queryParams.includeSpam = 'true';
                }
                if (params.addVerifiedStatus) {
                    queryParams.addVerifiedStatus = 'true';
                }
                if (params.includeCompressedOnSolana && network === Networks.Solana) {
                    queryParams.includeCompressed = 'true';
                }
                if (params.includeT22OnSolana && network === Networks.Solana) {
                    //   NO_TOKEN22_EXTENSION_TOKENS = 0, - Include all tokens
                    //   VANILLA = 1, - Include only tokens that are vanilla standard
                    //   WNS = 2, - Include tokens that are vanilla, and also WNS
                    //   UNRECOGNIZED = -1,
                    queryParams.token22StandardFilter = 1;
                }
                return this.request(`${this.#baseUrl}/${encodedNetwork}/${encodedAddress}/nfts`, queryParams);
            },
            getNft: (...args) => {
                return this.request(`${this.#baseUrl}/${encodedNetwork}/nfts/${args.map(encodeURIComponent).join('/')}`);
            },
            getNftImage: (...args) => {
                return this.request(`${this.#baseUrl}/${encodedNetwork}/nfts/${args.map(encodeURIComponent).join('/')}`);
            },
            getNftsTransactionsByAddress: (address) => {
                const encodedAddress = encodeURIComponent(address);
                return this.request(`${this.#baseUrl}/${encodedNetwork}/${encodedAddress}/transactions`);
            },
            getCollectionStats: ({ collectionSymbol, nftId, }) => {
                const { mintAddress, contractAddress, tokenId } = this.decomposeId(nftId);
                const address = (mintAddress || contractAddress || tokenId);
                const queryParams = {
                    address: encodeURIComponent(address),
                };
                if (collectionSymbol) {
                    queryParams.collectionSymbol = encodeURIComponent(collectionSymbol);
                }
                return this.request(`${this.#baseUrl}/${encodedNetwork}/collection-stats`, queryParams);
            },
        };
    }
}
export default NftsProxyApi;
